import { Component, HostListener, OnInit, OnDestroy, AfterViewInit, NgZone, ViewChild, ElementRef } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { CartService } from '../../../../shared/services/cart.service';
import { DataService } from 'src/shared/data/data.service';
import { ActivatedRoute } from '@angular/router';


// =====================================
// CUSTOMIZABLE ITEM CONFIG (Corporate Combos / Rice & Noodles)
// =====================================
export interface CustomChoice {
    label: string;
    price?: number;      // extra charge added to base price
}

export interface CustomGroup {
    key: string;
    label: string;
    type: 'single' | 'multi';   // single = pick exactly one, multi = optional add-ons
    required: boolean;
    choices: CustomChoice[];
}

export interface CustomConfig {
    key: string;
    basePrice: number;          // used only if the API gives no price
    priceFrom?: boolean;        // show "From $x" on the card
    hint: string;               // short line shown on the card
    groups: CustomGroup[];
    included?: string[];        // fixed items, no choice needed
}

export interface MenuGroup {
    id: string;
    name: string;
    image: string | null;
    items: any[];
    order?: number;      // original position, used to keep the order on mobile
}

const CUSTOM_CONFIGS: { match: RegExp; config: CustomConfig }[] = [
    {
        // Mid Day Fuel - $16.99 (Corporate Combo)
        match: /mid\s*day\s*fuel/i,
        config: {
            key: 'mid-day-fuel',
            basePrice: 16.99,
            hint: 'Choose your bread, curry & starter',
            groups: [
                {
                    key: 'bread', label: 'Choose 1 Bread', type: 'single', required: true,
                    choices: [{ label: 'Parotta' }, { label: 'Chapati' }, { label: 'Idly' }, { label: 'Kal Dosa' }]
                },
                {
                    key: 'curry', label: 'Choose 1 Curry', type: 'single', required: true,
                    choices: [{ label: 'Sambar' }, { label: 'Chicken Curry' }, { label: 'Veg Khurma' }]
                },
                {
                    key: 'starter', label: 'Choose 1 Starter / Bite', type: 'single', required: true,
                    choices: [{ label: 'Chicken 65' }, { label: 'Paneer 65' }, { label: 'Vada' }]
                }
            ],
            included: ['K.M. Salad', 'Salna & Raita', 'Sweet of the Day']
        }
    },
    {
        // Fried Rice / Noodles - ONE item, price updates with selection
        match: /fried\s*rice\s*(\/|&|and|or)\s*noodles?/i,
        config: {
            key: 'fried-rice-noodles',
            basePrice: 7.99,
            priceFrom: true,
            hint: 'Pick rice or noodles, and your protein',
            groups: [
                {
                    key: 'base', label: 'Choose 1', type: 'single', required: true,
                    choices: [{ label: 'Fried Rice' }, { label: 'Noodles' }]
                },
                {
                    key: 'protein', label: 'Choose 1', type: 'single', required: true,
                    choices: [
                        { label: 'Veg' },
                        { label: 'Egg', price: 1.00 },
                        { label: 'Chicken', price: 2.00 }
                    ]
                },
                {
                    key: 'addon', label: 'Add-on', type: 'multi', required: false,
                    choices: [{ label: 'Schezwan', price: 0.50 }]
                }
            ]
        }
    }
];

@Component({
    selector: 'app-projects-page',
    templateUrl: './projects-page.component.html',
    styleUrls: ['./projects-page.component.scss']
})
export class ProjectsPageComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('categoryBar', { static: false }) categoryBar!: ElementRef;
    @ViewChild('productsSection') productsSection!: ElementRef;

    activeCategory: number | null = null;
    projects: any[] = [];
    filteredProjects: any[] = [];
    selectedCategory: string = '';
    projectCategories: any[] = [];

    // Location and Hours
    location: string = '15171 Los Gatos Blvd, Los Gatos';
    hours: string = 'Fri–Sun 2–9 PM';
    directionsUrl: string = 'https://www.google.com/maps/search/?api=1&query=15171+Los+Gatos+Blvd,+Los+Gatos,+CA';

    cartItems: any[] = [];

    searchQuery: string = '';
    vegOnly: boolean = false;
    isCategorySticky: boolean = false;

    paginatedProjects: any[] = [];

    // Accordion menu state
    menuGroups: MenuGroup[] = [];
    accColumns: MenuGroup[][] = [[], []];   // left / right column on desktop
    expandedGroups = new Set<string>();
    private accInitialised = false;
    private preFilterExpanded: Set<string> | null = null;   // open dropdowns before a search / veg filter started

    // Sticky category / veg bar – set to true to bring it back
    showFilterBar = false;

    // Customizer (combo / rice-noodles) state
    customItem: any = null;
    customSelections: { [groupKey: string]: string[] } = {};
    customQty: number = 1;

    constructor(
        private meta: Meta,
        private titleService: Title,
        private http: HttpClient,
        private cartService: CartService,
        private data: DataService,
        private route: ActivatedRoute,
        private ngZone: NgZone
    ) { }
 
    // =====================================
    // DOWNLOAD MENU
    // =====================================

    downloadMenu(): void {
        const link = document.createElement('a');
        link.href = 'assets/menu/Kitchen Magic Menu - new timing.pdf';
        link.download = 'Kitchen Magic Menu.pdf';
        link.click();
    }

    // =====================================
    // LOAD LOCATION SETTINGS
    // =====================================

    loadLocationSettings(): void {
        this.http.post<any>(this.data.base_url + 'get_location_settings/', {}).subscribe({
            next: (response) => {
                if (response.status === 1 && response.data) {
                    this.location = response.data.location;
                    this.hours = response.data.hours;
                    this.directionsUrl = response.data.directions_url;
                }
            },
            error: (err) => {
                console.error('Error loading location settings:', err);
                // Keep default values if API fails
            }
        });
    }

    // =====================================
    // LIFECYCLE
    // =====================================

    ngOnInit(): void {
        this.titleService.setTitle('Our Menu – Kitchen Magic');
        this.meta.updateTag({
            name: 'description',
            content: 'Discover the delicious variety of dishes at Kitchen Magic, prepared with fresh ingredients and authentic flavors.'
        });

        this.filteredProjects = [];
        this.selectedCategory = '';
        // Load all projects at once for one-page menu
        this.getProjects();
        this.loadLocationSettings();
        this.cartService.cartItems$.subscribe(items => {
            this.syncProductQuantities(items);
        });

        // Check for category query parameter
        this.route.queryParams.subscribe(params => {
            if (params['category']) {
                setTimeout(() => {
                    this.filterProjects(params['category']);
                }, 500);
            }
        });
    }

    ngAfterViewInit(): void {
        // Scroll listeners run OUTSIDE Angular's zone so scrolling never triggers
        // change detection (which re-ran every template method for every card).
        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('scroll', this.onScrollBound, { passive: true });
            window.addEventListener('resize', this.measureStuckDelta, { passive: true });
            window.addEventListener('load', this.measureStuckDelta);
        });
        setTimeout(this.measureStuckDelta, 0);
        setTimeout(this.measureStuckDelta, 800); // after category images load
    }

    ngOnDestroy(): void {
        document.body.style.overflow = '';
        cancelAnimationFrame(this.scrollAnimId);
        window.removeEventListener('scroll', this.onScrollBound);
        window.removeEventListener('resize', this.measureStuckDelta);
        window.removeEventListener('load', this.measureStuckDelta);
    }

    syncProductQuantities(cartItems: any[]): void {
        const sync = (product: any) => {
            // Customizable items are added with a unique cart id per selection,
            // so they never switch to the +/- control; we only show how many are in the cart.
            if (product.customConfig) {
                product.cartCount = cartItems
                    .filter(item => item.project_id === product.id)
                    .reduce((sum, item) => sum + (item.quantity || 0), 0);
                product.inCart = false;
                return;
            }
            const cartItem = cartItems.find(item => item.id === product.id);
            if (cartItem) {
                product.quantity = cartItem.quantity;
                product.inCart = true;
            } else {
                product.quantity = 1;
                product.inCart = false;
            }
        };
        this.projects.forEach(sync);
        this.filteredProjects.forEach(sync);
    }

    // =====================================
    // SCROLL FILTER (horizontal)
    // =====================================

    scrollFilter(direction: 'left' | 'right') {
        if (this.categoryBar?.nativeElement) {
            this.categoryBar.nativeElement.scrollBy({
                left: direction === 'left' ? -200 : 200,
                behavior: 'smooth'
            });
        }
    }

    // =====================================
    // GET CATEGORIES
    // =====================================

    getProjectCategories(): void {
        this.http.post<any>(this.data.base_url + 'get_project_categories/', {}).subscribe({
            next: (response) => {
                if (response.status == 1) {
                    const categories = response.data
                        .filter((item: any) => item.status == 1)
                        .map((item: any) => ({
                            id: item.id,
                            name: item.category_name,
                            image: item.category_image ? this.data.base_url + item.category_image.substring(1) : null,
                            icon: 'bx bxs-food-menu',
                            active: false
                        }));

                    this.projectCategories = [
                        { id: '', name: 'All', image: null, icon: 'bx bx-dish', active: true },
                        ...categories
                    ];
                }
            },
            error: (err) => console.error('Categories error:', err)
        });
    }

    // =====================================
    // GET MENU ITEMS
    // =====================================

    getProjects(category?: string): void {
        const payload = category && category !== '' && category !== 'all'
            ? { category }
            : {};

    this.http.post<any>(this.data.base_url + 'get_webproject_data/', payload).subscribe({
        next: (response) => {
            if (response.status == 1) {

                // ==========================
                // Categories
                // ==========================
                const categories = (response.categories || [])
                    .map((item: any) => ({
                        id: item.id,
                        name: item.category_name,
                        image: item.category_image
                            ? this.data.base_url +'media/'+ item.category_image
                            : null,
                        icon: 'bx bxs-food-menu',
                        active: false,
                        order: item.order || 0
                    }))
                    .sort((a: any, b: any) => a.order - b.order);

                this.projectCategories = [
                    {
                        id: '',
                        name: 'All',
                        image: null,
                        icon: 'bx bx-dish',
                        active: true,
                        order: -1
                    },
                    ...categories
                ];

                // ==========================
                // Projects
                // ==========================
                this.projects = (response.data || [])
                    .filter((p: any) => p.status == 1)
                    .map((p: any) => {
                        const customConfig = this.getCustomConfig(p);
                        const apiPrice = Number(p.price) || 0;
                        const size = p.size ? `(${p.size})` : '';
                        return {
                            id: p.id,
                            image: p.image
                                ? this.data.base_url + 'media/' + p.image
                                : 'assets/img/bg-1.1.jpg',
                            category: (p.category || '').toLowerCase().trim(),
                            title: p.title,
                            description: p.description || '',
                            price: apiPrice > 0 ? apiPrice : (customConfig ? customConfig.basePrice : 0),
                            sales_tax: p.sales_tax || 0,
                            size: size,
                            badge: p.badge || '',
                            dish_type: this.getDishType(p),
                            kid_friendly: (p.kid_friendly || '').toLowerCase().trim(),
                            spicy: (p.spicy || '').toLowerCase().trim(),
                            quantity: 1,
                            inCart: false,
                            justAdded: false,
                            customConfig: customConfig,
                            cartCount: 0,
                            systems: this.convertSystems(p.systems)
                        };
                    });

                this.applyFilters();

                this.syncProductQuantities(this.cartService.getCartItems());
            }
        },
        error: (err) => {
            console.error('Projects load error:', err);
        }
    });
}

    // =====================================
    // VEG ONLY & FILTERING
    // =====================================

    toggleVegOnly(): void {
        this.vegOnly = !this.vegOnly;
        this.applyFilters();
    }

    filterProjects(categoryId: string): void {
        this.selectedCategory = categoryId;
        this.projectCategories.forEach((cat: any) => cat.active = cat.id === categoryId);

        this.applyFilters();

        // open the dropdown of the chosen category
        if (categoryId && categoryId !== 'all' && !this.isFiltering) {
            this.expandedGroups = new Set([categoryId]);   // only this one stays open
            this.balanceColumns();
        }

        // if (this.productsSection?.nativeElement) {
        //     setTimeout(() => {
        //         const offset = 180; // height of fixed navbar + sticky category bar
        //         const elementPosition = this.productsSection.nativeElement.getBoundingClientRect().top + window.pageYOffset;
        //         const offsetPosition = Math.max(0, elementPosition - offset);

        //         window.scrollTo({
        //             top: offsetPosition,
        //             behavior: 'smooth'
        //         });
        //     }, 50);
        // }
        setTimeout(() => this.smoothScrollToGrid(), 0);
    }

    /**
     * True while a search is active. Searching hides empty categories and opens the matching ones.
     * Veg Only is deliberately NOT part of this: it only updates the dish counts and the dishes
     * inside the dropdowns, without opening or hiding any category.
     */
    get isFiltering(): boolean {
        return !!this.searchQuery;
    }

    /** True when a search or a single category narrows which categories are listed. */
    private get hasActiveFilter(): boolean {
        return this.isFiltering || (!!this.selectedCategory && this.selectedCategory !== 'all');
    }

    /** Total dishes currently visible. */
    get visibleDishCount(): number {
        return this.filteredProjects.length;
    }

    applyFilters(): void {
        let result = [...this.projects];

        // 1. Category Filter
        if (this.selectedCategory && this.selectedCategory !== 'all') {
            result = result.filter((item: any) => item.category === this.selectedCategory);
        }

        // 2. Veg Only Filter
        if (this.vegOnly) {
            result = result.filter((item: any) => this.isVeg(item.dish_type));
        }

        // 3. Search Filter - every word must appear in the title or description
        if (this.searchQuery) {
            const words = this.searchQuery.split(/\s+/).filter(Boolean);
            result = result.filter((item: any) => {
                const text = ((item.title || '') + ' ' + (item.description || '')).toLowerCase();
                return words.every(w => text.includes(w));
            });
        }

        this.filteredProjects = result;

        // remember the open dropdowns when a search starts
        if (this.isFiltering && !this.preFilterExpanded) {
            this.preFilterExpanded = new Set(this.expandedGroups);
        }

        this.buildMenuGroups();

        if (this.isFiltering) {
            // searching: show every matching category, open, so results are visible straight away
            this.expandedGroups = new Set(this.menuGroups.map(g => g.id));
        } else if (this.preFilterExpanded) {
            // search cleared: bring back what the user had open
            this.expandedGroups = this.preFilterExpanded;
            this.preFilterExpanded = null;
        }

        this.balanceColumns();
    }

    clearAllFilters(): void {
        this.searchQuery = '';
        this.vegOnly = false;
        this.selectedCategory = '';
        this.projectCategories.forEach((cat: any) => cat.active = cat.id === '');
        this.applyFilters();
    }

    // =====================================
    // ACCORDION (category dropdowns)
    // =====================================

    /**
     * Groups the filtered dishes by category (in category order) and splits them into 2 columns.
     * - No filter active: every category is listed (empty ones show a "coming soon" message).
     * - Search / single category active: only categories that have matching dishes.
     * - Veg Only: every category stays listed, its count just shows the veg dishes.
     */
    private buildMenuGroups(): void {
        const groups: MenuGroup[] = [];
        const used = new Set<any>();
        const hideEmpty = this.hasActiveFilter;

        for (const cat of this.projectCategories) {
            if (cat.id === '' || cat.id === 'all') continue;
            const catId = String(cat.id).toLowerCase().trim();
            const catName = String(cat.name || '').toLowerCase().trim();
            const items = this.filteredProjects.filter((i: any) => {
                const c = String(i.category || '').toLowerCase().trim();
                return c === catId || c === catName;
            });
            items.forEach((i: any) => used.add(i));

            if (items.length || !hideEmpty) {
                groups.push({ id: String(cat.id), name: cat.name, image: cat.image || null, items });
            }
        }

        // dishes whose category isn't in the category list
        const rest = this.filteredProjects.filter((i: any) => !used.has(i));
        if (rest.length) {
            groups.push({ id: '__other', name: 'Other', image: null, items: rest });
        }

        groups.forEach((g, idx) => g.order = idx);
        this.menuGroups = groups;

        // first load: open the first dropdown
        if (!this.accInitialised && groups.length && !this.isFiltering) {
            this.expandedGroups.add(groups[0].id);
            this.accInitialised = true;
        }

        this.balanceColumns();
    }

    /**
     * Desktop: spreads the dropdowns over the 2 columns by their current height,
     * so when one category opens (and gets tall) the remaining ones fill the free
     * space in the other column instead of leaving a gap.
     * Mobile: the columns are flattened in CSS and `order` keeps the original sequence.
     */
    private balanceColumns(): void {
        const HEADER = 80;      // closed dropdown incl. gap
        const ROW = 83;         // one dish row
        const EMPTY = 170;      // "coming soon" message

        const cols: MenuGroup[][] = [[], []];
        const heights = [0, 0];

        for (const g of this.menuGroups) {
            const open = this.expandedGroups.has(g.id);
            const h = HEADER + (open ? (g.items.length ? g.items.length * ROW : EMPTY) : 0);
            const target = heights[1] < heights[0] ? 1 : 0;   // shorter column, left on a tie
            cols[target].push(g);
            heights[target] += h;
        }

        this.accColumns = cols;
    }

    isGroupOpen(id: string): boolean {
        return this.expandedGroups.has(id);
    }

    toggleGroup(id: string): void {
        if (this.expandedGroups.has(id)) {
            this.expandedGroups.delete(id);
        } else if (this.isFiltering) {
            // while searching / filtering, let people open several results at once
            this.expandedGroups.add(id);
        } else {
            // opening one closes every other dropdown
            this.expandedGroups = new Set([id]);
        }
        this.balanceColumns();
    }

    trackByIndex(index: number): number { return index; }
    trackByGroup(_i: number, g: MenuGroup): string { return g.id; }
    trackByItem(_i: number, item: any): any { return item.id; }

    // =====================================
    // EVERYDAY SPECIAL (empty state)
    // =====================================

    /** True when the selected category is "Everyday Special" (matched by id or name). */
    get isEverydaySpecialSelected(): boolean {
        if (!this.selectedCategory) return false;
        const pattern = /everyday[\s_-]*special/i;
        if (pattern.test(String(this.selectedCategory))) return true;
        const cat = this.projectCategories.find((c: any) => c.id === this.selectedCategory);
        return !!cat && pattern.test(String(cat.name || ''));
    }

    // =====================================
    // GET CATEGORY ITEM COUNT
    // =====================================

    getCategoryItemCount(categoryId: string): number {
        let base = this.projects;
        if (categoryId !== '' && categoryId !== 'all') {
            base = base.filter((item: any) => item.category === categoryId);
        }
        if (this.vegOnly) {
            base = base.filter((item: any) => this.isVeg(item.dish_type));
        }
        return base.length;
    }

    // =====================================
    // SEARCH ITEMS
    // =====================================

    onSearchItems(event: any): void {
        this.searchQuery = (event.target.value || '').toLowerCase().trim();
        this.applyFilters();
    }

    applySearchFilter(): void {
        this.applyFilters();
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.applyFilters();
    }

    // =====================================
    // DISH TYPE & KIDS FRIENDLY HELPERS
    // =====================================

    getDishType(p: any): string {
        if (p.dish_type && String(p.dish_type).trim() !== '') {
            return String(p.dish_type).toLowerCase().trim();
        }
        const text = ((p.title || '') + ' ' + (p.description || '')).toLowerCase();
        if (/\b(chicken|mutton|fish|prawn|shrimp|lamb|goat|beef|pork|meat|crab|salmon|tuna|turkey|bacon|wing|wings)\b/.test(text)) {
            return 'non-veg';
        }
        if (/\b(egg|omelet|omelette|bhurji|anda)\b/.test(text)) {
            return 'egg';
        }
        return 'veg';
    }

    isVeg(dishType: string): boolean {
        if (!dishType) return false;
        const type = dishType.toLowerCase().trim();
        return type === 'veg';
    }

    isNonVeg(dishType: string): boolean {
        if (!dishType) return false;
        const type = dishType.toLowerCase().trim();
        return type === 'non-veg' || type === 'non_veg' || type === 'nonveg';
    }

    isEgg(dishType: string): boolean {
        if (!dishType) return false;
        const type = dishType.toLowerCase().trim();
        return type === 'egg';
    }

    isKidFriendly(item: any): boolean {
        if (!item) return false;
        if (item.kid_friendly && String(item.kid_friendly).trim() !== '') {
            const val = String(item.kid_friendly).toLowerCase().trim();
            if (val === 'yes' || val === '1' || val === 'true') return true;
            if (val === 'no' || val === '0' || val === 'false') return false;
        }
        const text = ((item.title || '') + ' ' + (item.description || '')).toLowerCase();
        return /\b(kid|kids|child|children|baby|mild|fries|nuggets)\b/.test(text);
    }

    isSpicy(item: any): boolean {
        if (item.spicy && String(item.spicy).trim() !== '') {
            const val = String(item.spicy).toLowerCase().trim();
            if (val === 'yes' || val === '1' || val === 'true') return true;
            if (val === 'no' || val === '0' || val === 'false') return false;
        }
        return false;
    }

    // =====================================
    // CART
    // =====================================

    addToCart(project: any): void {
        this.cartService.addToCart({
            ...project,
            quantity: 1,
            sales_tax: project.sales_tax || 0
        });
        project.justAdded = true;
        setTimeout(() => {
            project.justAdded = false;
        }, 1500);
    }

    // =====================================
    // CUSTOMIZER (dropdown / selector items)
    // =====================================

    getCustomConfig(p: any): CustomConfig | null {
        const title = String(p.title || '');
        const found = CUSTOM_CONFIGS.find(c => c.match.test(title));
        return found ? found.config : null;
    }

    openCustomizer(item: any): void {
        this.customItem = item;
        this.customSelections = {};
        this.customQty = 1;
        document.body.style.overflow = 'hidden';
    }

    closeCustomizer(): void {
        this.customItem = null;
        this.customSelections = {};
        this.customQty = 1;
        document.body.style.overflow = '';
    }

    isChoiceSelected(group: CustomGroup, choice: CustomChoice): boolean {
        return (this.customSelections[group.key] || []).includes(choice.label);
    }

    onChoiceClick(group: CustomGroup, choice: CustomChoice): void {
        const current = this.customSelections[group.key] || [];
        if (group.type === 'single') {
            this.customSelections[group.key] = [choice.label];
        } else {
            // multi: toggle on/off
            this.customSelections[group.key] = current.includes(choice.label)
                ? current.filter(l => l !== choice.label)
                : [...current, choice.label];
        }
    }

    /** Price of ONE meal: base price + every selected extra. */
    get customUnitPrice(): number {
        if (!this.customItem) return 0;
        let total = Number(this.customItem.price) || 0;
        for (const group of this.customItem.customConfig.groups as CustomGroup[]) {
            const picked = this.customSelections[group.key] || [];
            for (const choice of group.choices) {
                if (picked.includes(choice.label)) {
                    total += choice.price || 0;
                }
            }
        }
        return Math.round(total * 100) / 100;
    }

    /** Unit price x quantity. Updates automatically as selections or quantity change. */
    get customTotal(): number {
        return Math.round(this.customUnitPrice * this.customQty * 100) / 100;
    }

    increaseCustomQty(): void {
        if (this.customQty < 50) this.customQty++;
    }

    decreaseCustomQty(): void {
        if (this.customQty > 1) this.customQty--;
    }

    get customReady(): boolean {
        if (!this.customItem) return false;
        return (this.customItem.customConfig.groups as CustomGroup[])
            .filter(g => g.required)
            .every(g => (this.customSelections[g.key] || []).length > 0);
    }

    /** Names of required groups still not chosen (shown as helper text). */
    get customMissing(): string[] {
        if (!this.customItem) return [];
        return (this.customItem.customConfig.groups as CustomGroup[])
            .filter(g => g.required && !(this.customSelections[g.key] || []).length)
            .map(g => g.label);
    }

    addCustomToCart(): void {
        if (!this.customItem || !this.customReady) return;

        const item = this.customItem;
        const config: CustomConfig = item.customConfig;

        const selectedOptions = config.groups.map(g => ({
            group: g.label,
            values: this.customSelections[g.key] || []
        })).filter(o => o.values.length > 0);

        const summary = selectedOptions.map(o => o.values.join(' + ')).join(', ');
        const signature = selectedOptions
            .map(o => o.values.join('+'))
            .join('|')
            .toLowerCase()
            .replace(/[^a-z0-9|+]/g, '');

        this.cartService.addToCart({
            ...item,
            // Unique id per selection so different choices become separate cart lines,
            // and identical choices merge into one line.
            id: item.id + '__' + signature,
            project_id: item.id,
            title: item.title + ' (' + summary + ')',
            base_title: item.title,
            price: this.customUnitPrice,
            sales_tax: item.sales_tax || 0,
            selectedOptions: selectedOptions,
            includedItems: config.included || [],
            quantity: this.customQty
        });

        item.justAdded = true;
        setTimeout(() => { item.justAdded = false; }, 1500);
        this.closeCustomizer();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.customItem) this.closeCustomizer();
    }

    // =====================================
    // IMAGE ERROR
    // =====================================

    onImageError(event: any): void {
        event.target.src = 'assets/img/project1.jpg';
    }

    // =====================================
    // HELPERS
    // =====================================

    convertSystems(systems: any): string[] {
        try {
            if (!systems) return [];
            if (Array.isArray(systems)) return systems;
            if (typeof systems === 'string') {
                if (systems.includes('[')) return JSON.parse(systems.replace(/'/g, '"'));
                return systems.split(',').map((s: string) => s.trim());
            }
            return [];
        } catch { return []; }
    }

    // increaseQuantity(project: any): void { project.quantity++; }
    // decreaseQuantity(project: any): void { if (project.quantity > 1) project.quantity--; }


    increaseQuantity(project: any): void {

        const cartItems = this.cartService.cartItems.value;

        const index = cartItems.findIndex(
            item => item.id === project.id
        );

        if (index !== -1) {

            const newQty = cartItems[index].quantity + 1;

            this.cartService.updateQuantity(index, newQty);

            project.quantity = newQty;

        }

    }

    decreaseQuantity(project: any): void {

        const cartItems = this.cartService.cartItems.value;

        const index = cartItems.findIndex(
            item => item.id === project.id
        );

        if (index === -1) {
            return;
        }

        const currentQty = cartItems[index].quantity;

        if (currentQty > 1) {

            const newQty = currentQty - 1;

            this.cartService.updateQuantity(index, newQty);

            project.quantity = newQty;

        } else {

            this.cartService.removeFromCart(index);

            project.inCart = false;
            project.quantity = 1;

        }

    }


    // =====================================
    // SCROLL HANDLING (runs outside Angular zone)
    // =====================================

    private scrollTicking = false;
    private barEl: HTMLElement | null = null;
    private scrollAnimId = 0;

    private onScrollBound = (): void => {
        if (this.scrollTicking) return;
        this.scrollTicking = true;

        requestAnimationFrame(() => {
            this.scrollTicking = false;

            // Stuck = the bar has actually reached its sticky `top` offset.
            const bar = this.barEl ?? (this.barEl = document.querySelector('.filter-bar-sticky') as HTMLElement | null);
            let stuck = this.isCategorySticky;
            if (bar) {
                const top = parseFloat(getComputedStyle(bar).top) || 0;
                stuck = bar.getBoundingClientRect().top <= top + 1;
            }

            let active = this.activeCategory;
            for (const cat of this.projectCategories) {
                const el = document.getElementById('cat-' + cat.id);
                if (el) {
                    const r = el.getBoundingClientRect();
                    if (r.top <= 150 && r.bottom >= 150) active = cat.id;
                }
            }

            // Only re-enter Angular when something actually changed
            if (stuck !== this.isCategorySticky || active !== this.activeCategory) {
                this.ngZone.run(() => {
                    this.isCategorySticky = stuck;
                    this.activeCategory = active;
                });
            }
        });
    };

    // Measures how much shorter the bar gets when stuck and exposes it as a CSS
    // variable, so layout height stays constant and cards don't jump on pin.
    private measureStuckDelta = (): void => {
        const bar = document.querySelector('.filter-bar-sticky') as HTMLElement | null;
        if (!bar) return;
        const had = bar.classList.contains('is-stuck');
        bar.style.transition = 'none';
        bar.classList.remove('is-stuck');
        const full = bar.offsetHeight;
        bar.classList.add('is-stuck');
        const compact = bar.offsetHeight;
        if (!had) bar.classList.remove('is-stuck');
        bar.style.transition = '';
        bar.style.setProperty('--stuck-delta', Math.max(0, full - compact) + 'px');
    };

    private smoothScrollToGrid(): void {
        const grid = this.productsSection?.nativeElement as HTMLElement;
        if (!grid) return;

        cancelAnimationFrame(this.scrollAnimId);
        const stop = () => {
            cancelAnimationFrame(this.scrollAnimId);
            window.removeEventListener('wheel', stop);
            window.removeEventListener('touchstart', stop);
        };
        window.addEventListener('wheel', stop, { passive: true });
        window.addEventListener('touchstart', stop, { passive: true });

        const jump = (y: number) =>
            window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior });

        const step = () => {
            const bar = document.querySelector('.filter-bar-sticky') as HTMLElement | null;
            const barTop = bar ? parseFloat(getComputedStyle(bar).top) || 0 : 0;
            const barH = bar ? bar.offsetHeight : 0;

            // re-measured every frame, so the bar shrinking or the list changing can't throw it off
            const target = Math.max(0, grid.getBoundingClientRect().top + window.scrollY - barTop - barH - 12);
            const diff = target - window.scrollY;

            if (Math.abs(diff) < 1) { jump(target); stop(); return; }

            const move = Math.abs(diff * 0.14) < 1 ? Math.sign(diff) : diff * 0.14;
            jump(window.scrollY + move);
            this.scrollAnimId = requestAnimationFrame(step);
        };

        this.scrollAnimId = requestAnimationFrame(step);
    }

    isSpecialGroup(group: { name?: string }): boolean {
        return /everyday[\s_-]*special/i.test(String(group?.name || ''));
    }

}
